/**
 * Pure state helpers for the MainNavMenu admin editor (Mirror redesign).
 * CommonJS on purpose: plain-Node tested on Node 14 (no test runner here).
 *
 * Droppable id contract (one DragDropContext in MainNavMenu):
 *   items                     top-level items bar            (type ITEM)
 *   cities-<item>             city tabs of one item          (type CITY)
 *   cols-<item>-<city>        columns of one city            (type COL)
 *   links-<item>-<city>-<col> links of one column            (type LINK)
 *   secondary                 the secondaryLinks list        (type LINK)
 */
const LANGS = ['en', 'es', 'ca', 'pt'];

const emptyText = () => ({ en: '', es: '', ca: '', pt: '' });

const linkTemplate = () => ({ text: emptyText(), url: '', visible: true });

const columnTemplate = () => ({ title: emptyText(), visible: true, links: [] });

const cityTemplate = () => ({
  key: '',
  visible: true,
  title: emptyText(),
  caption: emptyText(),
  cta: { text: emptyText(), url: '' },
  homeLink: { text: emptyText(), url: '' },
  columns: [],
});

const itemTemplate = () => ({
  key: '', text: emptyText(), link: '', visible: true, hasDropdown: false, cities: [],
});

const updateAt = (arr, index, value) => arr.map((v, i) => (i === index ? value : v));

const removeAt = (arr, index) => arr.filter((v, i) => i !== index);

const reorder = (arr, from, to) => {
  if (from === to || from < 0 || from >= arr.length || to < 0 || to >= arr.length) return arr;
  const next = [...arr];
  const [taken] = next.splice(from, 1);
  next.splice(to, 0, taken);
  return next;
};

// Languages whose value is empty in a localized {en,es,ca,pt} object.
const missingLangs = text => LANGS.filter(lang => !(text && text[lang] && String(text[lang]).trim()));

const getLinksAt = (data, droppableId) => {
  if (droppableId === 'secondary') return data.secondaryLinks || [];
  const parts = droppableId.split('-');
  return data.items[Number(parts[1])].cities[Number(parts[2])].columns[Number(parts[3])].links || [];
};

const setLinksAt = (data, droppableId, links) => {
  if (droppableId === 'secondary') return { ...data, secondaryLinks: links };
  const parts = droppableId.split('-');
  const i = Number(parts[1]);
  const c = Number(parts[2]);
  const k = Number(parts[3]);
  const item = data.items[i];
  const city = item.cities[c];
  const column = city.columns[k];
  return {
    ...data,
    items: updateAt(data.items, i, {
      ...item,
      cities: updateAt(item.cities, c, {
        ...city,
        columns: updateAt(city.columns, k, { ...column, links }),
      }),
    }),
  };
};

// One reducer for every onDragEnd result. data = { items, secondaryLinks }.
// Returns the same reference when nothing moved.
const applyDrag = (data, result) => {
  const { type, source, destination } = result;
  if (!destination) return data;
  if (source.droppableId === destination.droppableId && source.index === destination.index) return data;

  if (type === 'ITEM') {
    return { ...data, items: reorder(data.items, source.index, destination.index) };
  }
  if (type === 'CITY') {
    const i = Number(source.droppableId.split('-')[1]);
    const item = data.items[i];
    return {
      ...data,
      items: updateAt(data.items, i, { ...item, cities: reorder(item.cities || [], source.index, destination.index) }),
    };
  }
  if (type === 'COL') {
    const parts = source.droppableId.split('-');
    const i = Number(parts[1]);
    const c = Number(parts[2]);
    const item = data.items[i];
    const city = item.cities[c];
    return {
      ...data,
      items: updateAt(data.items, i, {
        ...item,
        cities: updateAt(item.cities, c, { ...city, columns: reorder(city.columns || [], source.index, destination.index) }),
      }),
    };
  }
  if (type === 'LINK') {
    if (source.droppableId === destination.droppableId) {
      const links = getLinksAt(data, source.droppableId);
      return setLinksAt(data, source.droppableId, reorder(links, source.index, destination.index));
    }
    const srcLinks = [...getLinksAt(data, source.droppableId)];
    const moved = srcLinks.splice(source.index, 1)[0];
    if (!moved) return data;
    const withoutSrc = setLinksAt(data, source.droppableId, srcLinks);
    const dstLinks = [...getLinksAt(withoutSrc, destination.droppableId)];
    dstLinks.splice(destination.index, 0, moved);
    return setLinksAt(withoutSrc, destination.droppableId, dstLinks);
  }
  return data;
};

module.exports = {
  LANGS,
  emptyText,
  linkTemplate,
  columnTemplate,
  cityTemplate,
  itemTemplate,
  updateAt,
  removeAt,
  reorder,
  missingLangs,
  applyDrag,
};
